import React, { useState, useMemo, useEffect } from 'react';
import { router, usePage } from '@inertiajs/react';
import { StablePagination } from '../../components/StablePagination';
import {
  Container,
  Title,
  Text,
  Button,
  TextInput,
  MultiSelect,
  Table,
  Checkbox,
  Modal,
  Loader,
  Pagination,
  Avatar,
  Group,
  MantineProvider,
  ActionIcon,
  Tooltip,
  Drawer,
  Stack,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconX, IconTrash, IconEdit, IconEye } from '@tabler/icons-react';
import EditUser from '../../pages/Users/EditUser';
import { useMediaQuery } from '@mantine/hooks';

interface User {
  id: number;
  username: string;
  email: string;
  confirmed: boolean;
  avatarUrl?: string;
  roles: string[];
  eventsCount: number;
  artistsCount: number;
  slug?: string;
}

interface IndexUsersProps {
  users: User[];
  currentUser: { id: number; superadmin: boolean };
  availableRoles: string[];
  query?: string;
}

const IndexUsers: React.FC<IndexUsersProps> = ({ users, currentUser, availableRoles, query: initialQuery = '' }) => {
  // Client-side pagination state
  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);

  // Other states
  const [searchQuery, setSearchQuery] = useState<string>(initialQuery || '');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof User | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const [drawerMode, setDrawerMode] = useState<'show' | 'edit'>('show');
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [selectedUserSlug, setSelectedUserSlug] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const isMobile = useMediaQuery('(max-width: 768px)');

  // Add debug tracking for drawer state
  useEffect(() => {
    console.log('Drawer state changed:', {
      drawerOpened,
      drawerMode,
      selectedUserSlug,
    });
  }, [drawerOpened, drawerMode, selectedUserSlug]);

  // Client-side filtering by search query and selected roles
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(
        (user) =>
          user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // Filter by selected roles
    if (selectedRoles.length > 0) {
      filtered = filtered.filter((user) => user.roles.some((role) => selectedRoles.includes(role)));
    }

    return filtered;
  }, [users, selectedRoles, searchQuery]);

  // Client-side sorting
  const sortedUsers = useMemo(() => {
    if (!sortColumn) return filteredUsers;
    return [...filteredUsers].sort((a, b) => {
      let valA = a[sortColumn];
      let valB = b[sortColumn];
      // Compare strings
      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      // Compare numbers
      if (typeof valA === 'number' && typeof valB === 'number') {
        return sortDirection === 'asc' ? valA - valB : valB - valA;
      }
      // Compare booleans
      if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        return sortDirection === 'asc' ? (valA === valB ? 0 : valA ? -1 : 1) : valA === valB ? 0 : valA ? 1 : -1;
      }
      return 0;
    });
  }, [filteredUsers, sortColumn, sortDirection]);

  // Calculate total pages based on sorted users
  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);

  // Get the current page users by slicing the sorted list
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedUsers, currentPage]);

  // Handle sorting when a column header is clicked
  const handleSort = (column: keyof User) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Handle checkbox selection
  const handleCheckboxChange = (userId: number, checked: boolean) => {
    if (checked) {
      setSelectedUserIds((prev) => [...prev, userId]);
    } else {
      setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
    }
  };

  // Trigger deletion modal for selected users
  const confirmDeletion = () => {
    setDeleteModalOpen(true);
  };

  // Delete selected users via Inertia request
  const handleDelete = () => {
    setIsLoading(true);
    router.delete('/users/destroy_multiple', {
      data: { user_ids: selectedUserIds },
      onSuccess: () => {
        showNotification({
          title: 'Success',
          message: 'Users deleted successfully',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
        setSelectedUserIds([]);
      },
      onError: () => {
        showNotification({
          title: 'Error',
          message: 'Failed to delete users',
          color: 'red',
          icon: <IconX size={16} />,
        });
      },
      onFinish: () => {
        setIsLoading(false);
        setDeleteModalOpen(false);
      },
    });
  };

  // Handle individual user deletion
  const handleDeleteSingleUser = (userId: number) => {
    setIsLoading(true);
    router.delete('/users/destroy_multiple', {
      data: { user_ids: [userId] },
      onSuccess: () => {
        showNotification({
          title: 'Success',
          message: 'User deleted successfully',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      },
      onError: () => {
        showNotification({
          title: 'Error',
          message: 'Failed to delete user',
          color: 'red',
          icon: <IconX size={16} />,
        });
      },
      onFinish: () => {
        setIsLoading(false);
        setDeleteModalOpen(false);
      },
    });
  };

  // Handle exporting emails
  const handleExportEmails = () => {
    setIsLoading(true);
    router.get(
      '/users/export_emails',
      {},
      {
        onSuccess: () => {
          showNotification({
            title: 'Success',
            message: 'Emails exported successfully',
            color: 'green',
            icon: <IconCheck size={16} />,
          });
        },
        onError: () => {
          showNotification({
            title: 'Error',
            message: 'Failed to export emails',
            color: 'red',
            icon: <IconX size={16} />,
          });
        },
        onFinish: () => {
          setIsLoading(false);
        },
      },
    );
  };

  // Function to handle opening the edit drawer
  const handleOpenEditDrawer = (user: User) => {
    console.log('Opening edit drawer for user:', user);
    if (!user.slug) {
      console.error('Cannot open drawer - user has no slug');
      return;
    }

    // Set all the necessary state for the drawer
    setSelectedUser(user);
    setSelectedUserSlug(user.slug);
    setDrawerMode('edit');
    setDrawerOpened(true);
  };

  // Function to close the drawer
  const handleCloseDrawer = () => {
    setDrawerOpened(false);
    setTimeout(() => {
      setSelectedUserSlug(null);
      setSelectedUser(null);
    }, 300); // Clean up state after drawer close animation
  };

  useEffect(() => {
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [searchQuery, selectedRoles]);

  return (
    <MantineProvider>
      <Container size="sm" style={{ backgroundColor: '#3d3c3e10', color: '#f2f2f2', padding: '20px' }}>
        <Group justify="space-between">
          <Title order={1} className="rubik-header mb-2">
            User Listing
          </Title>

          <Button onClick={handleExportEmails} style={{ backgroundColor: '#EA580C' }}>
            Export Emails
          </Button>
        </Group>
        <Text size="sm" style={{ color: '#f2f2f2' }}>
          Currently displaying {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}.
        </Text>

        <TextInput
          label="Search by username or email:"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          mt={20}
          styles={{
            label: {
              color: '#f2f2f2',
            },
          }}
          variant="filled"
        />

        <MultiSelect
          data={availableRoles.map((role) => ({ value: role, label: role }))}
          label="Filter by role"
          placeholder="Select roles"
          value={selectedRoles}
          hidePickedOptions
          onChange={setSelectedRoles}
          variant="filled"
          mt={20}
          mb={20}
          styles={{
            label: {
              color: '#f2f2f2',
            },
          }}
        />

        {isLoading && <Loader color="orange" variant="bars" style={{ marginTop: 20 }} />}
        <StablePagination
          value={currentPage}
          total={totalPages}
          color="#EA580C"
          onChange={(page) => setCurrentPage(page)}
        />

        {!isMobile ? (
          <div style={{ overflowX: 'auto' }}>
            <Table
              highlightOnHover
              style={{
                marginTop: 20,
                color: '#f2f2f2',
                minWidth: 600,
                borderSpacing: '0 12px',
                borderCollapse: 'separate',
              }}
              styles={{
                root: {
                  width: '100%',
                  tableLayout: 'fixed',
                },
                td: {
                  padding: '22px 18px',
                  verticalAlign: 'middle',
                  backgroundColor: '#171717',
                  borderRadius: '8px',
                },
                th: {
                  padding: '12px 8px',
                },
              }}
            >
              <thead>
                <tr>
                  <th></th>
                  <th onClick={() => handleSort('username')} style={{ cursor: 'pointer' }}>
                    Username
                  </th>
                  <th onClick={() => handleSort('confirmed')} style={{ cursor: 'pointer' }}>
                    Status
                  </th>
                  <th>Role</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user) => (
                  <tr key={user.id} className="mb-2">
                    <td>
                      <Checkbox
                        checked={selectedUserIds.includes(user.id)}
                        onChange={(e) => handleCheckboxChange(user.id, e.currentTarget.checked)}
                        color="orange"
                      />
                    </td>
                    <td>
                      <Group gap="sm" wrap="nowrap" align="center">
                        <Avatar
                          src={user.avatarUrl || 'https://dancewise.s3.amazonaws.com/WCWD+blank+profile2.png'}
                          radius="xl"
                        />
                        <Text truncate>{user.username}</Text>
                      </Group>
                    </td>
                    <td>
                      <Tooltip label={user.confirmed ? 'Confirmed' : 'Unconfirmed'}>
                        {user.confirmed ? <IconCheck size={20} color="green" /> : <IconX size={20} color="red" />}
                      </Tooltip>
                    </td>
                    <td>
                      <Button
                        variant="filled"
                        size="xs"
                        style={{
                          backgroundColor: '#3d3c3e',
                          border: '1px solid #EA580C',
                          color: '#f2f2f2',
                        }}
                      >
                        {user.roles.join(', ')}
                      </Button>
                    </td>
                    <td>
                      <Group gap="xs" justify="center">
                        {/* Action buttons here */}
                      </Group>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <Stack gap="sm" mt="md">
            {paginatedUsers.map((user) => (
              <div
                key={user.id}
                style={{
                  borderRadius: '12px',
                  padding: '16px',
                  backgroundColor: '#3d3c3e',
                }}
              >
                <Group align="center" gap="8px">
                  <Avatar
                    src={user.avatarUrl || 'https://dancewise.s3.amazonaws.com/WCWD+blank+profile2.png'}
                    radius="xl"
                  />
                  <div style={{ flex: 1 }}>
                    <Group gap="xs" align="center">
                      <Text fw={600} size="sm">
                        {user.username}
                      </Text>
                      <Tooltip label={user.confirmed ? 'Confirmed' : 'Unconfirmed'}>
                        {user.confirmed ? <IconCheck size={16} color="green" /> : <IconX size={16} color="red" />}
                      </Tooltip>
                    </Group>
                    <Text size="xs" color="dimmed">
                      {user.email}
                    </Text>
                    <Text size="xs" mt={4}>
                      Roles: {user.roles.join(', ')}
                    </Text>
                  </div>
                </Group>
                <Group gap="xs" mt="4px" justify="flex-end">
                  {user.slug && (
                    <Tooltip label="View User">
                      <ActionIcon component="a" href={`/users/${user.slug}`} color="#EA580C" variant="light" size="md">
                        <IconEye size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}

                  {(currentUser.id === user.id || currentUser.superadmin) && (
                    <Tooltip label="Edit User">
                      <ActionIcon
                        color="#EA580C"
                        variant="light"
                        size="md"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleOpenEditDrawer(user);
                        }}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}

                  {currentUser.superadmin && (
                    <Tooltip label="Delete User">
                      <ActionIcon
                        color="red"
                        variant="light"
                        size="md"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setUserToDelete(user);
                          setDeleteModalOpen(true);
                        }}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </div>
            ))}
          </Stack>
        )}

        <Button onClick={confirmDeletion} color="red" style={{ marginTop: 10, marginBottom: 20 }}>
          Delete selected users
        </Button>

        <Modal opened={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Confirm Deletion" centered>
          {userToDelete ? (
            <Text>Are you sure you want to delete user "{userToDelete.username}"?</Text>
          ) : (
            <Text>Are you sure you want to delete the selected users?</Text>
          )}
          <Group gap="apart" style={{ marginTop: 20 }}>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setUserToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => {
                if (userToDelete) {
                  handleDeleteSingleUser(userToDelete.id);
                  setUserToDelete(null);
                } else {
                  handleDelete();
                }
              }}
            >
              Confirm
            </Button>
          </Group>
        </Modal>

        <StablePagination
          value={currentPage}
          total={totalPages}
          color="#EA580C"
          onChange={(page) => setCurrentPage(page)}
        />
      </Container>

      {/* Enhanced Drawer Component */}
      <Drawer
        opened={drawerOpened}
        onClose={handleCloseDrawer}
        size="lg"
        padding="xl"
        title={drawerMode === 'show' ? 'View User' : 'Edit User'}
        position="right"
        overlayProps={{ opacity: 0.55, blur: 4 }}
        styles={{
          root: { zIndex: 1000 },
          header: {
            backgroundColor: '#18181B',
            color: '#f2f2f2',
            borderBottom: '1px solid #333',
          },
          body: {
            backgroundColor: '#18181B',
            color: '#f2f2f2',
            minHeight: '100vh',
            paddingTop: '2rem',
          },
          close: {
            color: '#f2f2f2',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
          },
        }}
      >
        {drawerMode === 'edit' && selectedUserSlug && (
          <EditUser
            user={selectedUser}
            update_url={`/users/${selectedUserSlug}`}
            available_roles={availableRoles.map((r) => ({ value: r, label: r }))}
            onSuccess={(updatedData) => {
              showNotification({
                title: 'Success',
                message: 'User updated successfully',
                color: 'green',
                icon: <IconCheck size={16} />,
              });
              handleCloseDrawer();
            }}
          />
        )}
      </Drawer>
    </MantineProvider>
  );
};

export default IndexUsers;
